import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Settings, User, HelpCircle, CreditCard, LogOut, ChevronDown, DollarSign, UserCheck, Handshake, Building2, Bell, Crown, Sparkles, Rocket, Star, MessageSquare, Users } from 'lucide-react';
import UserAvatar from './UserAvatar';

/**
 * Get account type label and styling based on user properties
 */
const getAccountType = (user) => {
  if (!user) return { label: 'Standard Free', color: 'text-gray-500', bgColor: 'bg-gray-100', icon: null };
  
  // VIP has highest priority
  if (user.is_vip) {
    return { 
      label: 'VIP', 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-100',
      icon: Crown
    };
  }
  
  // Founder Circle
  if (user.is_founder) {
    return { 
      label: 'Founder Circle', 
      color: 'text-amber-600', 
      bgColor: 'bg-amber-100',
      icon: Star
    };
  }
  
  // Early Adopter
  if (user.is_early_adopter) {
    return { 
      label: 'Early Adopter', 
      color: 'text-blue-600', 
      bgColor: 'bg-blue-100',
      icon: Rocket
    };
  }
  
  // Standard Premium (upgraded standard user)
  if (user.is_standard_premium) {
    return { 
      label: 'Standard Premium', 
      color: 'text-indigo-600', 
      bgColor: 'bg-indigo-100',
      icon: Sparkles
    };
  }
  
  // Also check subscription_tier for backwards compatibility
  const premiumTiers = ['premium_monthly', 'premium_yearly', 'premium'];
  if (premiumTiers.includes(user.subscription_tier) || premiumTiers.includes(user.subscription_state)) {
    return { 
      label: 'Standard Premium', 
      color: 'text-indigo-600', 
      bgColor: 'bg-indigo-100',
      icon: Sparkles
    };
  }
  
  // Standard Free (default free user)
  return { 
    label: 'Standard Free', 
    color: 'text-gray-500', 
    bgColor: 'bg-gray-100',
    icon: null
  };
};

/**
 * User Dropdown Menu Component
 * Shows user avatar with dropdown menu containing navigation options
 * Uses portal to render dropdown at document root to avoid z-index stacking issues
 */
const UserDropdownMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Update dropdown position when button is clicked
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user is an affiliate, coach, or partner
  const isAffiliate = user?.roles?.includes('affiliate') || user?.roles?.includes('affiliate_partner');
  const isCoach = user?.roles?.includes('coach') || user?.roles?.includes('marketplace_partner');
  const isOrganizationPartner = user?.roles?.includes('organization_partner');
  const isStrategicPartner = user?.roles?.includes('strategic_partner');
  const isClinicalSupportPartner = user?.roles?.includes('clinical_support_partner');
  
  // Check if user has any partner role
  const hasAnyPartnerRole = isAffiliate || isCoach || isOrganizationPartner || isStrategicPartner || isClinicalSupportPartner;

  const menuItems = [
    {
      label: 'Account Settings',
      icon: Settings,
      onClick: () => {
        navigate('/settings');
        setIsOpen(false);
      }
    },
    {
      label: 'Profile',
      icon: User,
      onClick: () => {
        navigate('/profile');
        setIsOpen(false);
      }
    },
    {
      label: 'Messages',
      icon: MessageSquare,
      onClick: () => {
        navigate('/cohort-messages');
        setIsOpen(false);
      }
    },
    {
      label: 'My Cohorts',
      icon: Users,
      onClick: () => {
        navigate('/my-cohorts');
        setIsOpen(false);
      }
    },
    {
      label: 'Notification Settings',
      icon: Bell,
      onClick: () => {
        navigate('/settings/notifications');
        setIsOpen(false);
      }
    },
    {
      label: 'Help & Support',
      icon: HelpCircle,
      onClick: () => {
        navigate('/support');
        setIsOpen(false);
      }
    },
    {
      label: 'Pricing',
      icon: CreditCard,
      onClick: () => {
        navigate('/pricing');
        setIsOpen(false);
      }
    },
    {
      label: 'Affiliate Program',
      icon: DollarSign,
      onClick: () => {
        // Always route to affiliate program info page (dashboard is in admin header)
        navigate('/affiliate-program');
        setIsOpen(false);
      }
    },
    {
      label: 'Become a Coach',
      icon: UserCheck,
      onClick: () => {
        navigate('/become-a-coach');
        setIsOpen(false);
      }
    },
    // Only show "Become an Organization Partner" if user is not already an approved organization partner
    ...(isOrganizationPartner ? [] : [{
      label: 'Become an Organization Partner',
      icon: Building2,
      onClick: () => {
        navigate('/become-an-organization-partner');
        setIsOpen(false);
      }
    }]),
    {
      label: hasAnyPartnerRole ? 'Partnership Dashboard' : 'Become a Partner',
      icon: Handshake,
      onClick: () => {
        // Route to dashboard if approved partner, otherwise to partnership program page
        navigate(hasAnyPartnerRole ? '/partner-dashboard' : '/partnership-program');
        setIsOpen(false);
      }
    },
    {
      label: 'Logout',
      icon: LogOut,
      onClick: () => {
        setIsOpen(false);
        onLogout();
      },
      danger: true
    }
  ];

  // Get account type for display
  const accountType = getAccountType(user);
  const AccountIcon = accountType.icon;

  return (
    <div className="relative">
      {/* Avatar Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-full"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="hidden sm:block text-right">
          <span className="text-sm font-medium text-gray-700 block">
            Welcome, {user?.name || 'User'}
          </span>
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <span className="text-xs text-gray-400">Account Type:</span>
            <span className={`text-xs font-semibold ${accountType.color} flex items-center gap-0.5`}>
              {AccountIcon && <AccountIcon className="w-3 h-3" />}
              {accountType.label}
            </span>
          </div>
        </div>
        <UserAvatar name={user?.name} size="default" />
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {/* Dropdown Menu - Rendered in Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 animate-in fade-in slide-in-from-top-2 duration-200"
          style={{ 
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            zIndex: 99999
          }}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isLast = index === menuItems.length - 1;
              
              return (
                <React.Fragment key={item.label}>
                  {isLast && <div className="my-1 border-t border-gray-100" />}
                  <button
                    onClick={item.onClick}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      item.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserDropdownMenu;
